import { AppData, Colors, Lightning, Registry } from '@lightningjs/sdk';
import { CcSettings } from 'config/platforms/deviceIntegration';
import { convertHexColor, ttsTagRemoval } from 'support/generalUtils';
import { constants } from 'aliases';
import TextLabel from 'components/common/TextLabel';

interface TextTrackDisplayTemplateSpec
  extends Lightning.Component.TemplateSpec {
  Text: typeof TextLabel;
}

const SCREEN_WIDTH = 1920;
const SCREEN_HEIGHT = 1080;
const DEFAULT_HEIGHT = 150;
const TEXT_PADDING = 5;
const FONT_SIZE = 40;
const FLASH_INTERVAL = constants.timers.subtitleFlashingInterval;

const getDefaultTextColor = (alpha = 1) => Colors('text').alpha(alpha).get();
const getHighlightTextColor = (alpha = 0.5) =>
  Colors('black').alpha(alpha).get();

export default class TextTrackDisplay
  extends Lightning.Component<TextTrackDisplayTemplateSpec>
  implements
    Lightning.Component.ImplementTemplateSpec<TextTrackDisplayTemplateSpec>
{
  private _paddingV = 0;
  private _flashOn = true;
  private _subtitleFlashingInterval: number | null = null;
  private _cueChangeListeners: Array<{
    track: TextTrack;
    listener: () => void;
  }> = [];
  private _activeTrackIndex: number | null = null;
  private _addTrackListener: (() => void) | null = null;
  private _currentTextTracks: TextTrackList | null = null;

  private _Text = this.getByRef('Text')!;

  static override _template() {
    return {
      h: DEFAULT_HEIGHT,
      w: SCREEN_WIDTH,
      y: SCREEN_HEIGHT,
      visible: true,
      mountY: 1,
      Text: {
        type: TextLabel,
        align: 'center',
        paddingLeft: TEXT_PADDING,
        paddingRight: TEXT_PADDING,
        y: 4,
        text: {
          text: '',
          textColor: getDefaultTextColor(),
          highlight: true,
          highlightColor: getHighlightTextColor(),
          fontSize: FONT_SIZE,
          lineHeight: FONT_SIZE * 1.7,
        },
      },
    };
  }

  initializeCaption(videoElement: HTMLVideoElement) {
    if (!videoElement) return;

    this.setDeviceStyle();

    this._Text.on('txLoaded', () => {
      this.h = this._Text.finalH + 2 * this._paddingV;
      this._Text.x = (SCREEN_WIDTH - this._Text.finalW) / 2;
    });

    this.setupTextTrackListeners(videoElement);
  }

  override _disable() {
    this.reset();
    if (this._subtitleFlashingInterval !== null) {
      Registry.clearInterval(this._subtitleFlashingInterval);
    }

    this._Text.removeAllListeners('txLoaded');

    this.clearTextTrackListeners();
  }

  private setupFlashingStyles(
    settings: Partial<CcSettings>,
    appliedStyles: Lightning.textures.TextTexture.Settings,
  ) {
    if (this._subtitleFlashingInterval !== null) {
      Registry.clearInterval(this._subtitleFlashingInterval);
    }

    const { colorFlashing, backgroundFlashing } = settings;

    if (!colorFlashing && !backgroundFlashing) return;

    this._subtitleFlashingInterval = Registry.setInterval(() => {
      const styles: Lightning.textures.TextTexture.Settings = {};
      const appliedTextColor = appliedStyles.textColor ?? getDefaultTextColor();
      const appliedHighlightColor =
        appliedStyles.highlightColor ?? getHighlightTextColor();

      if (colorFlashing) {
        styles.textColor = this._flashOn ? appliedTextColor : 0x00ffffff;
      }

      if (backgroundFlashing) {
        styles.highlightColor = this._flashOn
          ? appliedHighlightColor
          : 0x00ffffff;
      }

      this._Text.patch({
        text: styles,
      });

      this._flashOn = !this._flashOn;
    }, FLASH_INTERVAL);
  }

  private setDeviceStyle() {
    if (!AppData) return;

    const settings = AppData.device.getCcSettings();

    const styles: Lightning.textures.TextTexture.Settings = {};

    if (settings.fontSize) {
      styles.fontSize = settings.fontSize;
      styles.lineHeight = settings.fontSize * 1.7;
    }
    styles.fontFace = settings.fontFace ?? 'Roboto';
    styles.fontStyle = 'normal';

    if (settings.color) {
      styles.textColor = convertHexColor(settings.color, settings.colorOpacity);
    } else if (settings.colorOpacity) {
      const alpha = Number(`0x${settings.colorOpacity}`) / 0xff;
      styles.textColor = getDefaultTextColor(alpha);
    }

    if (settings.backgroundColor) {
      styles.highlightColor = convertHexColor(
        settings.backgroundColor,
        settings.backgroundOpacity ?? '80',
      );
    } else if (settings.backgroundOpacity) {
      const alpha = Number(`0x${settings.backgroundOpacity}`) / 0xff;
      styles.highlightColor = getHighlightTextColor(alpha);
    }

    if (settings.edgeColor && settings.edgeType) {
      styles.shadow = true;
      styles.shadowColor = convertHexColor(settings.edgeColor);

      const shadowProps = settings.edgeType;
      const [offsetX, offsetY, blur] = shadowProps
        .split('px')
        .map(s => s.trim());

      styles.shadowOffsetX = Number(offsetX);
      styles.shadowOffsetY = Number(offsetY);
      styles.shadowBlur = Number(blur);
    } else {
      styles.shadow = false;
    }

    // Window settings are not feasible for this component and have been left out

    this._Text.patch({
      text: styles,
    });

    this.setupFlashingStyles(settings, styles);
  }

  reset() {
    this._activeTrackIndex = null;
    this._Text.patch({ text: { text: '' } });
  }

  private clearTextTrackListeners() {
    if (this._addTrackListener && this._currentTextTracks) {
      this._currentTextTracks.removeEventListener(
        'addtrack',
        this._addTrackListener,
      );

      this._addTrackListener = null;
      this._currentTextTracks = null;
    }

    this._cueChangeListeners.forEach(({ track, listener }) => {
      track.removeEventListener('cuechange', listener);
    });
    this._cueChangeListeners = [];
  }

  private setupTextTrackListeners(videoElement: HTMLVideoElement) {
    if (!videoElement) return;

    this.setupTrackListenersForTracks(videoElement);

    // Listen for addTrack event in case videoElement.textTracks gets added late
    const addTrackHandler = () => {
      this.setupTrackListenersForTracks(videoElement);
    };
    videoElement.textTracks.addEventListener('addtrack', addTrackHandler);
    this._addTrackListener = addTrackHandler;
    this._currentTextTracks = videoElement.textTracks;
  }

  private setupTrackListenersForTracks(videoElement: HTMLVideoElement) {
    this.clearTextTrackListeners();

    for (const track of videoElement.textTracks) {
      const listener = () => {
        this.updateTextTrackDisplay(videoElement);
      };
      track.addEventListener('cuechange', listener);
      this._cueChangeListeners.push({ track, listener });
    }
  }

  private updateTextTrackDisplay(videoElement: HTMLVideoElement) {
    if (!videoElement) return;

    const textTracks = videoElement.textTracks;
    let index: number | null = null;
    const activeTrack = Array.from(textTracks).find((track, i) => {
      index = i;
      return track.mode === 'showing' || i === this._activeTrackIndex;
    });

    if (activeTrack) {
      this.displayTextTrack(activeTrack);
      this._activeTrackIndex = index;
      activeTrack.mode = 'hidden'; // hide track in video element
    } else {
      this.reset();
    }
  }

  private displayTextTrack(textTrack: TextTrack | undefined) {
    if (!textTrack) return;

    const cues = textTrack.activeCues ?? [];
    const cueText: string[] = [];
    for (let i = 0; i < cues.length; i++) {
      const cue = (cues[i] as VTTCue)?.text ?? '';
      cueText.push(ttsTagRemoval(cue));
    }

    this._Text.patch({ text: { text: cueText.join('\n').trimStart() } });
  }
}
